/**
 * Classic → New look morph. The banner's miniature (dark rail + card) grows
 * to the geometry the inset shell mounts in during its sign-in entrance:
 * collapsed rail (3rem) flush left, card inset 8px beside it. The caller then
 * switches the shell with the entrance flagged, so the real sidebar picks up
 * exactly where the overlay stops and slides out to full width underneath it.
 *
 * Plain DOM on document.body, not React: the Classic layout (and the banner
 * that started this) unmounts mid-flight when the shell switches.
 */
const RAIL_WIDTH = 48; // --sidebar-width-icon (3rem)
const GAP = 8; // SidebarInset m-2
const CARD_RADIUS = 12; // rounded-xl
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const GROW_MS = 720;
const COVER_MS = 180; // backdrop goes opaque fast so the Classic page never ghosts through
const FADE_MS = 280;
const SETTLE_MAX_MS = 1200; // longest we hold the cover waiting for the new page to paint

// While set, the inset shell holds its sidebar slide-out until the reveal ends.
export const MORPH_ATTR = 'data-new-look-morph';
export const REVEALED_EVENT = 'lodgeic:new-look-revealed';

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function box(el, rect, styles) {
  Object.assign(el.style, {
    position: 'fixed',
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    margin: 0,
    boxSizing: 'border-box',
    ...styles,
  });
  return el;
}

/**
 * Resolve once the inset shell is mounted and its page has stopped showing
 * loading placeholders (or after SETTLE_MAX_MS), so the reveal uncovers a
 * finished page rather than skeletons.
 */
function whenShellSettled() {
  const started = performance.now();
  return new Promise((resolve) => {
    let frames = 0;
    const check = () => {
      frames += 1;
      const card = document.querySelector('.app-shell-card');
      const busy = card && card.querySelector('[aria-busy="true"], [class*="skeleton" i], [class*="Skeleton"]');
      if ((card && !busy && frames > 2) || performance.now() - started > SETTLE_MAX_MS) resolve();
      else requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });
}

function reveal() {
  document.documentElement.removeAttribute(MORPH_ATTR);
  window.dispatchEvent(new Event(REVEALED_EVENT));
}

/**
 * @param {{ rail: Element, card: Element }} from  the miniature's parts
 * @param {() => void} onCovered  runs once the overlay fills the screen (switch shell + route here)
 */
export function playNewLookTransition(from, onCovered) {
  if (prefersReducedMotion() || !from?.rail || !from?.card || typeof document.body.animate !== 'function') {
    onCovered();
    return;
  }

  const root = getComputedStyle(document.documentElement);
  const railColor = root.getPropertyValue('--sidebar-background').trim() || '#0B0B0D';
  const cardColor = root.getPropertyValue('--lodge-white').trim() || '#FFFFFF';
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  document.documentElement.setAttribute(MORPH_ATTR, '1');
  const overlay = document.createElement('div');
  overlay.setAttribute('aria-hidden', 'true');
  Object.assign(overlay.style, { position: 'fixed', inset: '0', zIndex: '2147483000', pointerEvents: 'none' });

  // Page behind the morph dims to the inset shell's backdrop colour
  const backdrop = box(document.createElement('div'), { left: 0, top: 0, width: vw, height: vh }, {
    background: railColor,
    opacity: '0',
  });

  const r0 = from.rail.getBoundingClientRect();
  const c0 = from.card.getBoundingClientRect();
  const rail = box(document.createElement('div'), r0, { background: railColor, borderRadius: '5px' });
  const card = box(document.createElement('div'), c0, {
    background: cardColor,
    borderRadius: '5px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
    overflow: 'hidden',
  });
  // The miniature's placeholder lines ride along and fade as the card grows
  const lines = from.card.cloneNode(true);
  Object.assign(lines.style, { position: 'absolute', inset: '0', background: 'transparent', boxShadow: 'none' });
  card.appendChild(lines);

  overlay.append(backdrop, rail, card);
  document.body.appendChild(overlay);

  const railTo = { left: 0, top: 0, width: RAIL_WIDTH, height: vh };
  const cardTo = { left: RAIL_WIDTH + GAP, top: GAP, width: vw - RAIL_WIDTH - 2 * GAP, height: vh - 2 * GAP };
  const frame = (r, radius) => ({
    left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px`, borderRadius: `${radius}px`,
  });
  const timing = { duration: GROW_MS, easing: EASE, fill: 'forwards' };

  const grow = [
    backdrop.animate([{ opacity: 0 }, { opacity: 1 }], { duration: COVER_MS, easing: 'ease-out', fill: 'forwards' }),
    // The miniature's rail is a dark illustration; it settles on the colour
    // the viewer's sidebar will actually be (Porcelain by default).
    rail.animate([
      { ...frame(r0, 5), background: getComputedStyle(from.rail).backgroundColor },
      { ...frame(railTo, 0), background: railColor },
    ], timing),
    card.animate([frame(c0, 5), frame(cardTo, CARD_RADIUS)], timing),
    lines.animate([{ opacity: 1 }, { opacity: 0 }], { ...timing, duration: GROW_MS * 0.5 }),
  ];

  Promise.all(grow.map((a) => a.finished)).catch(() => {}).then(async () => {
    onCovered();
    // The shell mounts under the cover in exactly this geometry (collapsed
    // rail + inset card); hold until its page has painted, uncover it, and only
    // then let the sidebar slide out, so nothing moves beneath a half-faded layer.
    await whenShellSettled();
    overlay.animate([{ opacity: 1 }, { opacity: 0 }], { duration: FADE_MS, easing: 'ease-out', fill: 'forwards' })
      .finished.catch(() => {}).then(() => {
        overlay.remove();
        reveal();
      });
  });
}
